import { StoryBible, Ledger, Chapter, SoftStateBrief, SeriesCanon } from '../src/types';
import { resolveChapterTitle } from './chapterTitle.service';
import { formatCanonEntriesForPrompt, formatCanonLocksForPrompt } from '../src/utils/canonInputNormalization.service';
import { buildPhysicalContinuityLookbackSection } from '../src/utils/physicalContinuity.service';

/**
 * Compiles a deterministic narrative context block mapping onto the SoftStateBrief interface.
 * This guarantees character/location coherence, saves significant API tokens,
 * and maintains structural continuity across chapters and sequel books.
 */
export function compileDeterministicContext(
  storyBible: StoryBible,
  ledger: Ledger,
  chapterNum: number,
  previousChapters: Chapter[],
  seriesCanon?: SeriesCanon
): SoftStateBrief {
  const currentPlan = storyBible.chapterPlans?.find(p => p?.chapterNumber === chapterNum);

  // 1. Chapter Title & Roadmap details
  const approvedTitle = resolveChapterTitle(null, currentPlan, chapterNum);
  const pov = currentPlan?.pov || 'Omniscient';
  const requiredBeats = currentPlan?.requiredBeats || [];
  const beatsText = requiredBeats.length > 0
    ? requiredBeats.map(b => `- ${b}`).join('\n')
    : 'No specific required beats listed.';

  // 2. Active Location & Characters from Ledger
  const activeLocations = Object.values(ledger.locations || {})
    .filter((l: any) => l.status?.toLowerCase().includes('active') || l.active === true)
    .map((l: any) => l.name || l.title);
  const activeLocationStr = activeLocations.length > 0 ? activeLocations.join(', ') : 'Not specified';

  const activeCharacters = Object.values(ledger.characters || {})
    .filter((c: any) => {
      const status = (c.status || '').toLowerCase();
      const isActive = status.includes('active') || status.includes('alive');
      const isInActiveLocation = c.location && activeLocations.includes(c.location);
      return isActive || isInActiveLocation;
    })
    .map((c: any) => c.name);
  const activeCharsStr = activeCharacters.length > 0 ? activeCharacters.join(', ') : 'None active';

  // 3. Last 3 chapter summaries
  const last3Chapters = previousChapters.slice(-3);
  const summariesText = last3Chapters.length > 0
    ? last3Chapters.map(c => `Chapter ${c.id} (${c.title}): ${c.summary || 'No summary available.'}`).join('\n')
    : 'First chapter of the volume.';

  // 4. Relevant unresolved threads
  const unresolvedThreads = ledger.plot_points || [];
  const unresolvedText = unresolvedThreads.length > 0
    ? formatCanonEntriesForPrompt(unresolvedThreads, { sourceField: 'ledger.plot_points', separator: '\n', emptyLabel: '' })
        .split('\n')
        .filter(Boolean)
        .map((line) => `- ${line}`)
        .join('\n')
    : 'No outstanding unresolved plot threads in ledger.';

  // 5. Current character statuses (Live registry)
  const charStatuses = Object.entries(ledger.characters || {})
    .map(([name, data]: [string, any]) => `${name}: ${data.role || 'Secondary'} (${data.status || 'Active'})`)
    .join('\n');

  // 6. Dead/Unavailable Character Registry
  const deadOrUnavailable = Object.entries(ledger.characters || {})
    .filter(([_, data]: [string, any]) => {
      const status = (data.status || '').toLowerCase();
      return status.includes('dead') || status.includes('deceased') || status.includes('killed') || status.includes('unavailable');
    })
    .map(([name, data]: [string, any]) => `- ${name} (${data.status})`);
  const deadRegistryText = deadOrUnavailable.length > 0
    ? deadOrUnavailable.join('\n')
    : 'All registered characters are alive and available.';

  // 7. Forbidden Names/Canon Locks
  const canonLocks = (storyBible as any).canonLocks || [];
  const locksFormatted = formatCanonLocksForPrompt(canonLocks, 'storyBible.canonLocks');
  const locksText = locksFormatted
    ? locksFormatted.split('\n').filter(Boolean).map((line) => `- ${line}`).join('\n')
    : 'No explicit canon locks enforced.';

  // 8. Series Canon Facts & Sequel info
  const sequelInfo = storyBible.volume > 1
    ? `Sequel Volume ${storyBible.volume} active. Previous book ending state is preserved in series canon timeline.`
    : 'Standalone volume (Book 1) active.';

  const seriesFacts = seriesCanon?.globalWorldRules && seriesCanon.globalWorldRules.length > 0
    ? seriesCanon.globalWorldRules.map(r => `- ${r}`).join('\n')
    : 'No global series rules registered.';

  // Construct structured data map within the standard SoftStateBrief fields
  const dramatizationRequirements = requiredBeats.length > 0
    ? requiredBeats.map((beat, idx) => {
        return `Beat ${idx + 1}: "${beat}"
- REQUIRED EVENT: Dramatize "${beat}" as a complete scene.
- CHARACTERS INVOLVED: ${activeCharsStr}
- EMOTIONAL PURPOSE: Force internal character processing and authentic reaction to this event.
- SCENE FUNCTION: Progress the chapter plot step by step without skipping transitions.
- EXPANSION INSTRUCTION: Treat Beat ${idx + 1} as a full scene moment, not a sentence or summary. Show the character moving through the environment, reacting physically and emotionally, and discovering the beat through action.`;
      }).join('\n\n')
    : 'No specific required beats listed.';

  const physicalContinuityLookback = buildPhysicalContinuityLookbackSection(ledger, currentPlan, chapterNum);

  return {
    recentSensoryCarryover: `[LAST 3 CHAPTERS SUMMARY]\n${summariesText}`,
    activeEmotionalResidue: `[ACTIVE STATUS]\nPOV: ${pov}\nActive Location: ${activeLocationStr}\nActive Cast: ${activeCharsStr}\n\n[ALL REGISTRY STATUS]\n${charStatuses}`,
    voiceNotes: `[SERIES CONTINUITY & CONTEXT]\n${sequelInfo}\n\n[GLOBAL LORE RULES]\n${seriesFacts}`,
    environmentalContinuityCues: `[ROADMAP CHAPTER OUTLINE]\nApproved Title: ${approvedTitle}\nRequired Beats:\n${beatsText}\n\n[DRAMATIZATION REQUIREMENTS]\n${dramatizationRequirements}${physicalContinuityLookback ? `\n\n${physicalContinuityLookback}` : ''}`,
    motifsToLightlyPreserve: `[PLOT CONTEXT & THREADS]\nUnresolved Threads:\n${unresolvedText}`,
    antiRepetitionWarnings: `[CANON LOCKS & DECEASED CAST]\nForbidden Actions/Lore Rules:\n${locksText}\n\nDeceased/Unavailable Registry:\n${deadRegistryText}`
  };
}
